import { useEffect, useState } from "react";
import { X, Users } from "lucide-react";
import { PlusIcon } from "@radix-ui/react-icons";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import SharedSelect from "@/components/shared/select";
import { Badge } from "@/components/ui/badge";
import { FilterRule } from "@/components/filter-rule";
import styles from "@/routes/simulator/release/styles.module.scss";
import { useSimulator } from "@/v2/features/simulator/context/SimulatorWrapper";
import { toast } from "sonner";
import { useDebouncedCallback } from "@/lib/useDebouncedCallback";

type Rule = {
  id: string;
  field: string;
  operator: string;
  value: string[];
};

type FilterGroup = {
  id: string;
  combinator: "and" | "or";
  rules: Rule[];
};

export function AdvancedFilters({
  setLoadingFilters,
}: {
  setLoadingFilters: (loading: boolean) => void;
}) {
  const { currentRelease, updateRelease } = useSimulator();

  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>(
    currentRelease?.filterPolicy
      ? typeof currentRelease.filterPolicy === "string"
        ? JSON.parse(currentRelease.filterPolicy)
        : currentRelease.filterPolicy
      : []
  );

  const addGroup = () => {
    const newGroup: FilterGroup = {
      id: `group-${Date.now()}`,
      combinator: "and",
      rules: [
        { id: `rule-${Date.now()}`, field: "name", operator: "is", value: [] },
      ],
    };
    setFilterGroups([...filterGroups, newGroup]);
  };

  const removeGroup = (groupId: string) => {
    const updatedGroups = filterGroups.filter((group) => group.id !== groupId);
    setFilterGroups(updatedGroups);
  };

  const addRule = (groupId: string) => {
    setFilterGroups(
      filterGroups.map((group) => {
        if (group.id === groupId) {
          return {
            ...group,
            rules: [
              ...group.rules,
              {
                id: `rule-${Date.now()}`,
                field: "name",
                operator: "is",
                value: [],
              },
            ],
          };
        }
        return group;
      })
    );
  };

  const removeRule = (groupId: string, ruleId: string) => {
    setFilterGroups(
      filterGroups.map((group) => {
        if (group.id === groupId) {
          // Don't remove if it's the last rule
          if (group.rules.length === 1) return group;

          return {
            ...group,
            rules: group.rules.filter((rule) => rule.id !== ruleId),
          };
        }
        return group;
      })
    );
  };

  const updateRule = (
    groupId: string,
    ruleId: string,
    updatedRule: Partial<Rule>
  ) => {
    setFilterGroups(
      filterGroups.map((group) => {
        if (group.id === groupId) {
          return {
            ...group,
            rules: group.rules.map((rule) => {
              if (rule.id === ruleId) {
                return { ...rule, ...updatedRule };
              }
              return rule;
            }),
          };
        }
        return group;
      })
    );
  };

  const updateGroupCombinator = (groupId: string, combinator: "and" | "or") => {
    setFilterGroups(
      filterGroups.map((group) => {
        if (group.id === groupId) {
          return { ...group, combinator };
        }
        return group;
      })
    );
  };

  const applyFilters = () => {
    if (currentRelease?.id) {
      updateRelease(currentRelease?.id.toString(), {
        simulatorId: currentRelease?.simulatorId,
        filterPolicy: JSON.stringify(filterGroups),
      });
      setLoadingFilters(false);
    } else {
      toast.error("Something went wrong, please try again");
    }
    // Here you would typically send these filters to your backend or apply them to your data
  };

  const debouncedApplyFilters = useDebouncedCallback(applyFilters, 2000);
  useEffect(() => {
    setLoadingFilters(true);
    debouncedApplyFilters();
  }, [filterGroups]);

  const resetFilters = () => {
    setFilterGroups([]);
  };

  const hasActiveFilters = filterGroups.some((group) =>
    group.rules.some((rule) => rule.value.length > 0)
  );
  const showFilterGroups = hasActiveFilters || filterGroups.length > 0;

  const getAudienceText = () => {
    if (!hasActiveFilters) return "All users";
    const totalRules = filterGroups.reduce(
      (acc, group) => acc + group.rules.length,
      0
    );
    return `${totalRules} filter${totalRules > 1 ? "s" : ""} applied`;
  };

  if (!currentRelease) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4">
        <Badge
          variant="outline"
          className="flex items-center gap-2 px-3 py-1.5 text-sm"
        >
          <Users size={16} />
          {getAudienceText()}
        </Badge>
        {!showFilterGroups && (
          <Button
            variant="outline"
            className={styles.actionButton}
            onClick={addGroup}
          >
            <PlusIcon />
            Add Filter
          </Button>
        )}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            className={`${styles.actionButton} ${styles.ghost}`}
            onClick={resetFilters}
          >
            Reset
          </Button>
        )}
      </div>

      {showFilterGroups && (
        <div className="space-y-2">
          {filterGroups?.map((group, groupIndex) => (
            <>
              <Card key={group.id} className="border border-gray-200 w-full">
                <CardHeader className="flex flex-row items-center justify-end py-1">
                  <Button
                    variant="ghost"
                    className={`${styles.actionButton} ${styles.ghost} h-6 w-6 p-0 mr-0.5`}
                    onClick={() => removeGroup(group.id)}
                  >
                    <X size={16} />
                    <span className="sr-only">Remove group</span>
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3 pt-0 w-full">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Matching</span>
                    <SharedSelect
                      options={[
                        { label: "All", value: "and" },
                        { label: "Any", value: "or" },
                      ]}
                      value={group.combinator}
                      onChange={(value) =>
                        updateGroupCombinator(group.id, value as "and" | "or")
                      }
                      classes={{ root: "w-[80px]" }}
                    />
                    <span className="text-sm">the following:</span>
                  </div>
                  <div className="space-y-2 w-full">
                    {group?.rules?.map((rule) => (
                      <FilterRule
                        key={rule.id}
                        rule={rule}
                        onUpdate={(updatedRule) =>
                          updateRule(group.id, rule.id, updatedRule)
                        }
                        onRemove={() => removeRule(group.id, rule.id)}
                      />
                    ))}
                  </div>
                  <div className="flex justify-start">
                    <Button
                      variant="outline"
                      className={styles.actionButton}
                      onClick={() => addRule(group.id)}
                    >
                      <PlusIcon className="mr-2 h-4 w-4" />
                      Add Filter
                    </Button>
                  </div>
                </CardContent>
              </Card>
              {groupIndex < filterGroups.length - 1 && (
                <div className="flex items-center gap-4 my-2">
                  <div className="h-[1px] bg-gray-200 flex-1" />
                  <span className="text-sm text-gray-500">OR</span>
                  <div className="h-[1px] bg-gray-200 flex-1" />
                </div>
              )}
            </>
          ))}

          {filterGroups.length > 0 && (
            <Button
              variant="outline"
              className={styles.actionButton}
              onClick={addGroup}
            >
              <PlusIcon />
              Add Filter Group
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
